import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { fullDate } from '../../pages/reports/reportDates'
import {
  acknowledgeSchedule,
  getPendingAcknowledgements,
  type PendingAcknowledgement,
} from '../../pages/agent/agentAcknowledgementStore'

/**
 * docs/product/scheduling-model.md's "Acknowledging a schedule": a published
 * version of the agent's team schedule they have not yet confirmed they have
 * seen, with one tap to confirm it. An amendment asks again. Shows nothing when
 * there is nothing to acknowledge.
 */
export default function ScheduleAcknowledgements() {
  const [userId, setUserId] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingAcknowledgement[]>([])
  const [confirmed, setConfirmed] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const load = useCallback(async (id: string) => {
    setPending(await getPendingAcknowledgements(id))
  }, [])

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id || cancelled) return
      setUserId(id)
      await load(id)
    })
    return () => {
      cancelled = true
    }
  }, [load])

  async function acknowledge(item: PendingAcknowledgement) {
    if (!userId) return
    const result = await acknowledgeSchedule(userId, item)
    if (!result.ok) {
      setFailed(result.message)
      return
    }
    setFailed(null)
    setConfirmed(`${item.periodLabel} acknowledged.`)
    await load(userId)
  }

  if (pending.length === 0 && !confirmed && !failed) return null

  return (
    <section aria-label="Schedule acknowledgements" className="flex flex-col gap-2">
      {pending.map((item) => (
        <div key={`${item.teamId}|${item.periodStart}|${item.version}`} className="flex flex-wrap items-center gap-3 rounded-surface border border-primary/30 bg-primary/5 p-3">
          <p className="min-w-0 flex-1 text-small font-bold">
            {item.periodLabel}, {fullDate(item.periodStart)} to {fullDate(item.periodEnd)}: your team&rsquo;s schedule was {item.amended ? 'amended' : 'published'}.
          </p>
          <button
            type="button"
            onClick={() => acknowledge(item)}
            className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
          >
            Acknowledge
            <span className="sr-only"> {item.periodLabel}</span>
          </button>
        </div>
      ))}
      {confirmed && (
        <p role="status" className="text-small font-bold text-success">
          {confirmed}
        </p>
      )}
      {failed && (
        <p role="alert" className="text-caption font-semibold text-error">
          {failed}
        </p>
      )}
    </section>
  )
}
